import { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { PublicClientApplication } from '@azure/msal-browser';
import { apiRequest } from '../config/authConfig';
import { getCurrentRoles } from '../contexts/PermissionContext';

/**
 * Setup authentication interceptor for Axios
 * Automatically adds Bearer token to all requests
 */
export const setupAuthInterceptor = (axiosInstance: AxiosInstance, msalInstance: PublicClientApplication) => {
  axiosInstance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length > 0) {
        try {
          // Try to acquire token silently for the API
          const response = await msalInstance.acquireTokenSilent({
            ...apiRequest,
            account: accounts[0],
          });

          // Add token to Authorization header
          if (config.headers) {
            config.headers.Authorization = `Bearer ${response.accessToken}`;
          }

          // Add user roles header for backend permission checks
          const roles = getCurrentRoles();
          if (roles.length > 0) {
            config.headers['X-User-Roles'] = roles.join(',');
          }

          console.log('Token acquired for API request:', response.accessToken.substring(0, 50) + '...');
        } catch (error) {
          console.error('Error acquiring token for API call:', error);
          // If silent token acquisition fails, try interactive
          try {
            const response = await msalInstance.acquireTokenPopup(apiRequest);
            if (config.headers) {
              config.headers.Authorization = `Bearer ${response.accessToken}`;
            }
            console.log('Token acquired via popup for API request');
          } catch (popupError) {
            console.error('Failed to acquire token via popup:', popupError);
          }
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Optional: Add response interceptor to handle 401 errors
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        console.warn('Received 401 Unauthorized. User may need to re-authenticate.');
        // Could trigger a re-login here if needed
      }
      return Promise.reject(error);
    }
  );
};
